import { useModalMeta } from "@/shared/atoms/useModal";
import useCategoryHook from "@/shared/hooks/useCategoryHook";
import useMenuItemHook from "@/shared/hooks/useMenuItemHook";
import { MenuItems } from "@/shared/utils/enum";
import { MenuDataTagKind, Topping } from "@/shared/utils/interfaces";
import { Col, Row } from "react-grid-system";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { arrayMove } from "react-sortable-hoc";
import {
  getLabel,
  guidGenerator,
  menuItemTypes,
  toppingContentTypeOptions,
} from "../../shared/utils/helpers";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import TextfieldIntControlled from "../Input/TextfieldIntControlled";
import MenuDataTagFields from "../Menu/MenuDataTagFields";
import Modal, { ModalProps } from "../Modal/Modal";
import ModalTitle from "../Modal/ModalTitle";
import Select from "../Select/Select";
import SortableSelect from "../Select/SortableSelect";

const MenuGarnishModal = ({ onExit, ...props }: ModalProps) => {
  const { t } = useTranslation();
  const [{ defaultValue, refetchGarnishes }] = useModalMeta();
  const { garnishRemove, garnishCreate, garnishUpdate } = useCategoryHook();
  const { data: toppings, isLoading } = useMenuItemHook<Topping[]>(
    MenuItems.TOPPING
  );

  const methods = useForm();
  const { handleSubmit, control, watch, setValue } = methods;

  const onFormSubmit = (data) => {
    const req = {
      ...defaultValue,
      ...data,
      toppings: (data.toppings || []).map((topping) => topping.item_id),
      code: defaultValue?.code || guidGenerator(),
    };
    if (defaultValue?.item_id === undefined) {
      garnishCreate.mutateAsync(
        { ...req, item_type: MenuItems.GARNISH },
        {
          onSuccess: () => {
            onExit();
            refetchGarnishes();
          },
        }
      );
    } else {
      garnishUpdate.mutateAsync(req, {
        onSuccess: () => {
          onExit();
          refetchGarnishes();
        },
      });
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const toppings = watch("toppings");
    const newValue = arrayMove(toppings, oldIndex, newIndex);
    setValue("toppings", newValue);
  };

  return (
    <Modal {...props} width={{ _: "90%", md: "100rem" }} onExit={onExit}>
      <ModalTitle>
        {defaultValue?.item_id
          ? t("garnishes.extraCategoryUpdate")
          : t("garnishes.extraCategoryAdd")}
      </ModalTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Row>
            <Col xs={12} md={6}>
              <div className="mb-[1rem]">
                <TextfieldIntControlled
                  label={t("garnishes.name")}
                  placeholder={t("enterValue")}
                  name="name"
                  defaultValue={defaultValue?.name || {}}
                  validationMessage={t("standardMustBeFilled")}
                  required
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="mb-[1rem]">
                <TextfieldIntControlled
                  label={t("garnishes.description")}
                  placeholder={t("enterValue")}
                  name="description"
                  defaultValue={defaultValue?.description || {}}
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="mb-[1rem]">
                <Controller
                  name="topping_content_type"
                  control={control}
                  defaultValue={defaultValue?.topping_content_type || 1}
                  render={(props) => {
                    return (
                      <Select
                        label={t("garnishes.method")}
                        getOptionLabel={(option) => t(`menu.${option.label}`)}
                        menuPortalTarget={document.getElementById("root")}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 1000000 }),
                        }}
                        onChange={(v) => props.onChange(v.value)}
                        isSearchable
                        value={toppingContentTypeOptions.find(
                          (option) => option.value === parseInt(props.value)
                        )}
                        options={toppingContentTypeOptions}
                      />
                    );
                  }}
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <Controller
                name="item_image_and_text_properties"
                control={control}
                defaultValue={defaultValue?.item_image_and_text_properties ?? 1}
                render={(props) => {
                  return (
                    <Select
                      label={t("type")}
                      getOptionLabel={(option) => t(`${option.label}`)}
                      menuPortalTarget={document.getElementById("root")}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 1000000 }),
                      }}
                      onChange={(v) => props.onChange(v.value)}
                      isSearchable
                      value={menuItemTypes.find(
                        (option) => option.value === parseInt(props.value)
                      )}
                      options={menuItemTypes}
                    />
                  );
                }}
              />
            </Col>
            <Col xs={12}>
              <div className="mb-[1rem]">
                <Controller
                  name="toppings"
                  control={control}
                  defaultValue={defaultValue?.toppings ?? []}
                  render={(props) => {
                    return (
                      <SortableSelect
                        onSortEnd={onSortEnd}
                        menuPortalTarget={document.getElementById("root")}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        onChange={(toppings) => props.onChange(toppings)}
                        label={t("garnishes.toppings")}
                        isMulti
                        isSearchable
                        options={toppings ?? []}
                        isLoading={isLoading}
                        getOptionLabel={(topping) => getLabel(topping)}
                        getOptionValue={(topping) => topping.item_id}
                        value={props.value}
                      />
                    );
                  }}
                />
              </div>
            </Col>
            <Col xs={12}>
              <div className="mb-[1rem]">
                <MenuDataTagFields
                  kind={MenuDataTagKind.TOPPING_CATEGORY}
                  defaultValue={defaultValue}
                  showSize
                />
              </div>
            </Col>
            {/*   <Col xs={12} md={6}>
              <TargetSelect
                control={control}
                defaultValue={defaultValue?.target}
              />
            </Col> */}
            <Col xs={12}>
              <div className="mt-12 flex w-full flex-wrap items-center justify-end">
                <div className="flex-[1_1_100%] md:flex-[unset] m-[1rem]">
                  <Controller
                    name="is_active"
                    control={control}
                    defaultValue={defaultValue?.is_active ?? true}
                    render={(props) => {
                      return (
                        <Checkbox
                          active={props.value}
                          checked={props.value}
                          onChange={(e) => props.onChange(e.target.checked)}
                        >
                          {t("garnishes.active")}
                        </Checkbox>
                      );
                    }}
                  />
                </div>

                {defaultValue?.item_id && (
                  <div className="flex-[1_0_auto] md:flex-[unset] m-[1rem]">
                    <Button
                      type="button"
                      isPending={garnishRemove.isPending}
                      onClick={() =>
                        garnishRemove.mutateAsync(defaultValue?.item_id, {
                          onSuccess: () => onExit(),
                        })
                      }
                      variant="danger"
                    >
                      {t("garnishes.delete")}
                    </Button>
                  </div>
                )}
                <div className="m-[1rem] flex-[1_0_auto] md:flex-[unset]">
                  <Button
                    isPending={
                      garnishCreate.isPending || garnishUpdate.isPending
                    }
                  >
                    {t("garnishes.save")}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default MenuGarnishModal;
